import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { FormControl } from '@mui/material'
import Select, { MultiValue } from 'react-select'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { getAuditsByMissionId } from '../../redux/audits/auditActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { useTranslation } from 'react-i18next'
import { createOptionProps } from '../../components/common/CreateOptionMenu'

interface SelectAuditProps {
  value: string[]
  onChange: (event: SelectChangeEvent<string[]>) => void
  MissionId: string
  disablePortalMenu?: boolean
  onCreateNew?: (search: string) => void
  reloadKey?: number
}

const SelectAudit: React.FC<SelectAuditProps> = ({
  value,
  onChange,
  MissionId,
  disablePortalMenu = false,
  onCreateNew,
  reloadKey = 0,
}) => {
  const menu = useExclusiveMenu(!disablePortalMenu)
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const auditsList = useSelector((state: RootState) => state.audit.audits)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [optionsLoading, setOptionsLoading] = useState(false)


  useEffect(() => {
    if (!MissionId) {
      setOptionsLoading(false)
      return
    }
    let active = true
    setOptionsLoading(true)
    dispatch(getAuditsByMissionId(MissionId)).finally(() => {
      if (active) setOptionsLoading(false)
    })
    return () => {
      active = false
    }
  }, [dispatch, MissionId, reloadKey])

  const options = Array.isArray(auditsList)
    ? auditsList.map((audit) => ({
        value: audit._id || '',
        label: audit.AuditName || '',
      }))
    : []

  const selectedOptions = options.filter((opt) => value?.includes(opt.value))

  const handleSelectChange = (
    selected: MultiValue<{ value: string; label: string }>
  ) => {
    onChange({
      target: { name: 'AuditId', value: selected ? selected.map((s) => s.value) : [] },
    } as SelectChangeEvent<string[]>)
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <FormControl fullWidth style={{ width: '100%' }}>
        <Select
          isMulti
          value={selectedOptions}
          onChange={handleSelectChange}
          options={options}
          isLoading={optionsLoading}
          placeholder={
            !MissionId
              ? t('actionImport.context.pickMissionFirst')
              : optionsLoading
                ? t('selectOptions.loading')
                : t('audits')
          }
          isDisabled={optionsLoading || !MissionId}
          noOptionsMessage={() =>
            !MissionId ? t('actionImport.context.pickMissionFirst') : t('tables.noData')
          }
          {...menu}
          styles={incyiaSelectStyles}
          {...createOptionProps(
            onCreateNew && MissionId
              ? { label: t('quickCreate.new.audit'), onCreate: onCreateNew }
              : undefined
          )}
        />
      </FormControl>
    </div>
  )
}

export default SelectAudit
